import { useState } from 'react'
import Map, { NavigationControl, MapRef, FullscreenControl, Marker } from 'react-map-gl/mapbox'
import ShopMarker from '../../../RouteManagement/RouteMap/ShopMarker'
import EndingLocationMarker from '../../../RouteManagement/RouteMap/EndingLocationMarker'
import MapStyleDropdown from '../MapStyleDropdown'
import MapShopAddress from '../../../RouteManagement/RouteView/MapShopAddress'
import ManageShopAddressModal from '../../../RouteManagement/RouteAdd/ManageShopAddressModal'
import ReorderDayViewMapLegend from './ReorderDayViewMapLegend'
import { ReorderDayViewState } from './reorderDayViewModel'
import { DEFAULT_MAP_ZOOM } from '../../../RouteManagement/RouteAdd/routeAddModel'
import { TenantLocation } from '../../../../domain/tenantLocations/tenantLocation'

type ReorderDayViewMapProps = {
  state: ReorderDayViewState
  mapRef: React.RefObject<MapRef>
  hoveredEventId: number | null
  setHoveredEventId: (id: number | null) => void
  setIsMapLoaded: (loaded: boolean) => void
  mapStyle: string
  setMapStyle: (style: string) => void
  isStyleChanging: boolean
  setIsStyleChanging: (changing: boolean) => void
  currentRouteData: any
  displayRouteOnMap: (routeData: any, animate: boolean) => void
  displayDrivingDirections: () => void
  shopLocation: any
  tenantLocations: TenantLocation[] | undefined
  onShopAddressUpdate: (coordinates: { latitude: number; longitude: number }) => void
}

const ReorderDayViewMap = ({
  state,
  mapRef,
  hoveredEventId,
  setHoveredEventId,
  setIsMapLoaded,
  mapStyle,
  setMapStyle,
  isStyleChanging,
  setIsStyleChanging,
  currentRouteData,
  displayRouteOnMap,
  displayDrivingDirections,
  shopLocation,
  tenantLocations,
  onShopAddressUpdate
}: ReorderDayViewMapProps) => {
  const [isManageShopAddressModalOpen, setIsManageShopAddressModalOpen] = useState(false)

  return (
    <div style={{ flex: 1, position: 'relative', background: '#eee' }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
        initialViewState={shopLocation ? {
          longitude: shopLocation.longitude,
          latitude: shopLocation.latitude,
          zoom: DEFAULT_MAP_ZOOM,
          bearing: 0,
          pitch: 0,
          padding: { top: 0, bottom: 0, left: 0, right: 0 }
        } : undefined}
        onLoad={() => {
          setIsMapLoaded(true)
          displayDrivingDirections()
        }}
        onStyleData={() => {
          if (isStyleChanging && currentRouteData) {
            setTimeout(() => {
              displayRouteOnMap(currentRouteData, false)
            }, 100)
            setIsStyleChanging(false)
          }
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
      >
        <NavigationControl position="top-left" />
        <FullscreenControl position="bottom-left" />

        <MapStyleDropdown
          mapStyle={mapStyle}
          setMapStyle={setMapStyle}
          setIsStyleChanging={setIsStyleChanging}
        />

        <MapShopAddress
          tenantLocations={tenantLocations}
          onManageShopAddress={() => setIsManageShopAddressModalOpen(true)}
        />

        {state.startingLocation && state.startingLocation.latitude && state.startingLocation.longitude && (
          <ShopMarker latitude={state.startingLocation.latitude} longitude={state.startingLocation.longitude} />
        )}

        {state.endingLocation && state.endingLocation.latitude && state.endingLocation.longitude &&
         (!state.startingLocation ||
          state.startingLocation.latitude !== state.endingLocation.latitude ||
          state.startingLocation.longitude !== state.endingLocation.longitude) && (
          <EndingLocationMarker latitude={state.endingLocation.latitude} longitude={state.endingLocation.longitude} />
        )}

        {state.events && state.events.length > 0 && state.events
          .map((event, index) => {
            if (!event.latitude || !event.longitude) return null
            if (state.startingLocation && state.startingLocation.id === `event-${event.id}`) return null
            if (state.endingLocation && state.endingLocation.id === `event-${event.id}`) return null

            const isHovered = hoveredEventId === event.id

            const markerStyle = {
              position: 'relative' as React.CSSProperties['position'],
              width: '30px',
              height: '45px',
              display: 'flex',
              flexDirection: 'column' as React.CSSProperties['flexDirection'],
              alignItems: 'center',
            }
            const pinHeadStyle = {
              width: '30px',
              height: '30px',
              backgroundColor: '#FFCABD',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              border: '2px solid #FF5C35',
              zIndex: 2,
              position: 'relative' as React.CSSProperties['position'],
              boxShadow: isHovered ? '0 0 16px 6px #FFCABD' : undefined,
              transition: 'box-shadow 0.2s, transform 0.2s',
              cursor: isHovered ? 'pointer' : 'default',
              transform: isHovered ? 'scale(1.15)' : 'scale(1)',
            }
            const pinTailStyle = {
              width: '20px',
              height: '20px',
              backgroundColor: '#FFCABD',
              clipPath: 'polygon(50% 100%, 0 0, 100% 0)',
              marginTop: '-5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              zIndex: 1,
            }

            return (
              <Marker
                key={event.id}
                latitude={event.latitude}
                longitude={event.longitude}
                anchor="bottom"
              >
                <div
                  style={markerStyle}
                  onMouseEnter={() => setHoveredEventId(event.id)}
                  onMouseLeave={() => setHoveredEventId(null)}
                >
                  <div style={pinHeadStyle}>
                    <span style={{ color: '#FF5C35', fontWeight: 'bold', fontSize: 14 }}>
                      {index + 1}
                    </span>
                  </div>
                  <div style={pinTailStyle} />
                </div>
              </Marker>
            )
          })}
      </Map>
      <ReorderDayViewMapLegend />
      <ManageShopAddressModal
        isOpen={isManageShopAddressModalOpen}
        onClose={() => setIsManageShopAddressModalOpen(false)}
        shopAddress={shopLocation}
        onAddressUpdate={onShopAddressUpdate}
      />
    </div>
  )
}

export default ReorderDayViewMap